'use client'

import { Disclaimer, PlainButton, Skeleton, Span } from '@pcln/horizon'
import { useCallback, useRef, type KeyboardEvent } from 'react'

import {
  AI_SEARCH_EXAMPLES_LABEL,
  AI_SEARCH_LOADING_RECOMMENDATIONS,
  AI_SEARCH_NO_RECOMMENDATIONS_AFTER_LINK,
  AI_SEARCH_NO_RECOMMENDATIONS_BEFORE_LINK,
  AI_SEARCH_NO_RECOMMENDATIONS_LINK_LABEL,
  AI_SEARCH_RECOMMENDATIONS_ERROR,
  AI_SEARCH_RECOMMENDATIONS_LABEL,
} from './aiSearchConstants'
import type { SuggestionChip, SuggestionsStatus } from './useAiSearchSuggestions'

const RECOMMENDATION_CHIP_BASE_CLASS =
  'shrink-0 cursor-pointer rounded-action-sm border px-2 py-[1px] text-body2 text-primary-13 transition-all'

const RECOMMENDATION_CHIP_DEFAULT_CLASS =
  'border-transparent bg-primary-1 shadow-md hover:bg-primary-3 hover:shadow-lg active:bg-primary-1'

const RECOMMENDATION_CHIP_SELECTED_CLASS = 'border-transparent bg-primary-3 shadow-md'

const SUGGESTION_CHIPS_CONTAINER_CLASS = 'relative w-full px-1 py-1 min-h-9 min-w-0'

const SUGGESTION_CHIP_SKELETON_WIDTHS = [148, 96, 188, 124]

type AiSearchSuggestionsProps = {
  isMobile?: boolean
  status: SuggestionsStatus
  suggestionChips: SuggestionChip[]
  selectedChipKey: string | null
  hasTypedQuery: boolean
  fetchQuery: string
  statusId: string
  labelId: string
  emptyId: string
  errorId: string
  onChipSelect: (chip: SuggestionChip) => void
  onSwitchToStandardSearch?: (searchQuery: string) => void
}

export function AiSearchSuggestions({
  isMobile = false,
  status,
  suggestionChips,
  selectedChipKey,
  hasTypedQuery,
  fetchQuery,
  statusId,
  labelId,
  emptyId,
  errorId,
  onChipSelect,
  onSwitchToStandardSearch,
}: Readonly<AiSearchSuggestionsProps>) {
  const chipButtonRefs = useRef<Array<HTMLButtonElement | null>>([])
  const chipRowClass = isMobile ? 'flex flex-wrap gap-2' : 'flex items-center gap-2'
  const chipsContainerClass = `${SUGGESTION_CHIPS_CONTAINER_CLASS} ${
    isMobile ? 'w-full' : 'overflow-x-auto overflow-y-hidden'
  }`

  const handleChipGroupKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return
      if (suggestionChips.length === 0) return

      event.preventDefault()

      const currentIndex = chipButtonRefs.current.indexOf(document.activeElement as HTMLButtonElement)
      const fallbackIndex = event.key === 'ArrowRight' ? -1 : 0
      const nextIndex =
        event.key === 'ArrowRight'
          ? (currentIndex + 1 + suggestionChips.length) % suggestionChips.length
          : (currentIndex - 1 + suggestionChips.length) % suggestionChips.length

      chipButtonRefs.current[currentIndex === -1 ? fallbackIndex : nextIndex]?.focus()
    },
    [suggestionChips.length],
  )

  return (
    <div
      className='flex w-full flex-col gap-2'
      id={statusId}
      role='status'
      aria-live='polite'
      data-testid='ai-search-suggestions-layout'
    >
      <Span id={labelId} bold textStyle='body3' palette='neutral' shade='13' className='w-full'>
        {hasTypedQuery ? AI_SEARCH_RECOMMENDATIONS_LABEL : AI_SEARCH_EXAMPLES_LABEL}
      </Span>

      <div data-testid='ai-search-chips-container' className={chipsContainerClass}>
        {status === 'loading' ? (
          <>
            <span className='sr-only'>{AI_SEARCH_LOADING_RECOMMENDATIONS}</span>
            <div data-testid='ai-search-suggestions-loading' className={chipRowClass} aria-hidden='true'>
              {SUGGESTION_CHIP_SKELETON_WIDTHS.map((width) => (
                <Skeleton key={width} className='rounded-action-sm' height={28} width={width} />
              ))}
            </div>
          </>
        ) : null}

        {status === 'chips' ? (
          <div
            role='radiogroup'
            aria-labelledby={labelId}
            tabIndex={-1}
            data-testid='ai-search-chip-group'
            className={chipRowClass}
            onKeyDown={handleChipGroupKeyDown}
          >
            {suggestionChips.map((chip, index) => {
              const isSelected = selectedChipKey === chip.key

              return (
                <button
                  key={chip.key}
                  ref={(element) => {
                    chipButtonRefs.current[index] = element
                  }}
                  type='button'
                  role='radio'
                  aria-checked={isSelected}
                  data-chip-key={chip.key}
                  className={`${RECOMMENDATION_CHIP_BASE_CLASS} ${
                    isSelected ? RECOMMENDATION_CHIP_SELECTED_CLASS : RECOMMENDATION_CHIP_DEFAULT_CLASS
                  }`}
                  onClick={() => onChipSelect(chip)}
                >
                  <span>{chip.label}</span>
                </button>
              )
            })}
          </div>
        ) : null}

        {status === 'error' ? (
          <Span id={errorId} textStyle='body3' palette='neutral' shade='10' className='px-0 py-0'>
            {AI_SEARCH_RECOMMENDATIONS_ERROR}
          </Span>
        ) : null}

        {status === 'empty' ? (
          <div id={emptyId}>
            <Disclaimer className='px-0 py-0'>
              {AI_SEARCH_NO_RECOMMENDATIONS_BEFORE_LINK}
              {onSwitchToStandardSearch ? (
                <PlainButton
                  onClick={() => onSwitchToStandardSearch(fetchQuery.trim())}
                  type='primary'
                  variant='inline'
                >
                  {AI_SEARCH_NO_RECOMMENDATIONS_LINK_LABEL}
                </PlainButton>
              ) : (
                AI_SEARCH_NO_RECOMMENDATIONS_LINK_LABEL
              )}
              {AI_SEARCH_NO_RECOMMENDATIONS_AFTER_LINK}
            </Disclaimer>
          </div>
        ) : null}
      </div>
    </div>
  )
}
